import React, { useState, useCallback } from 'react';
import type { AxiosInstance } from 'axios';
import { GenericTable } from '@ui-components/Table/GenericTable';
import GenericButton from '@ui-components/Button/GenericButton';
import { GenericTag } from '@ui-components/Tag/GenericTag';
import AccountLabelFormModal from './AccountLabelFormModal';
import type { FortiFlexAccountLabel } from '../hooks/useFortiFlexData';
import {
    createAccountLabel,
    updateAccountLabel,
} from '../request/request';

interface AccountLabelsTableProps {
    accountLabels: FortiFlexAccountLabel[];
    api: AxiosInstance;
    onMutate: () => void;
}

const AccountLabelsTable: React.FC<AccountLabelsTableProps> = ({
    accountLabels,
    api,
    onMutate,
}) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [editItem, setEditItem] = useState<FortiFlexAccountLabel | null>(null);
    const [saving, setSaving] = useState(false);

    const openCreate = () => {
        setEditItem(null);
        setModalOpen(true);
    };

    const openEdit = (item: FortiFlexAccountLabel) => {
        setEditItem(item);
        setModalOpen(true);
    };

    const handleSubmit = useCallback(
        async (data: { accountId: string; label: string }) => {
            setSaving(true);
            try {
                if (editItem) {
                    await updateAccountLabel(api, editItem.id, { label: data.label });
                } else {
                    await createAccountLabel(api, data);
                }
                setModalOpen(false);
                onMutate();
            } catch {
                // error visible via network; modal stays open
            } finally {
                setSaving(false);
            }
        },
        [api, editItem, onMutate]
    );

    const isBusy = saving;

    const columns = [
        {
            header: 'Account ID',
            accessor: 'accountId' as keyof FortiFlexAccountLabel,
            width: '150px',
        },
        {
            header: 'Label',
            accessor: 'label' as keyof FortiFlexAccountLabel,
            width: '200px',
        },
        {
            header: 'Auto-discovered',
            width: '140px',
            renderCell: (r: FortiFlexAccountLabel) => (
                <GenericTag
                    label={r.autoDiscovered ? 'Yes' : 'No'}
                    variant={r.autoDiscovered ? 'success' : 'neutral'}
                    size="small"
                    rounded="full"
                />
            ),
        },
        {
            header: 'Actions',
            width: '150px',
            renderCell: (r: FortiFlexAccountLabel) => (
                <GenericButton
                    variant="secondary"
                    size="sm"
                    label="Edit"
                    onClick={() => openEdit(r)}
                />
            ),
        },
    ];

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-semantic-text-primary">
                    Account Labels
                </h3>
                <GenericButton
                    variant="brand"
                    size="md"
                    label="Add Label"
                    onClick={openCreate}
                    disabled={isBusy}
                />
            </div>
            <GenericTable
                columns={columns}
                data={accountLabels}
                initialRowsPerPage={10}
            />
            <AccountLabelFormModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                onSubmit={handleSubmit}
                account={editItem}
                loading={saving}
            />
        </div>
    );
};

export default AccountLabelsTable;
